///
/// EarthBrowser
///
/// Created by Matt Giger on 9/3/2026
/// Copyright (c) 2026 Geodata Labs
///

import { sharedEmberSphereGeometry } from "@/earthbrowser/entities/geometries/EmberSphereGeometry"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type color } from "@stovokor/math/color"
import { type matrix } from "@stovokor/math/matrix"
import { type Geometry } from "@stovokor/shader/Geometry"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"

const cShockwavePeriod = 3 // seconds per expansion cycle

/**
 * An expanding, fading translucent shell that ripples outward from an
 * earthquake epicenter like a seismic wave. All motion is shader-driven
 * (no per-frame CPU work). Scale the node to the marker radius; the wave
 * expands from 1x to 2.6x that radius each cycle.
 */
export class QuakeShockwaveNode extends NodeBase {
	private waveRenderer: QuakeShockwaveRenderer

	constructor(context: StovokorContext, color: color) {
		super()
		this.waveRenderer = new QuakeShockwaveRenderer(context, sharedEmberSphereGeometry(context))
		this.waveRenderer.color.value = color
		this.renderer = this.waveRenderer
	}

	public set color(value: color) {
		this.waveRenderer.color.value = value
	}

	/** Overall fade multiplier (0..1) */
	public set opacity(value: number) {
		this.waveRenderer.opacity.value = value
	}

	/** Minimum apparent size in radians, matching the ember dome (0 = disabled) */
	public set minAngularSize(value: number) {
		this.waveRenderer.minAngular.value = value
	}
}

class QuakeShockwaveRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public modelView = new UniformDynamic<matrix>()
	public color = new UniformDynamic<color>()
	public opacity = new UniformDynamic<number>(1)
	public period = new UniformDynamic<number>(cShockwavePeriod)
	public time = new UniformDynamic<number>(0)
	public minAngular = new UniformDynamic<number>(0)

	constructor(context: StovokorContext, geometry: Geometry) {
		// Pass a transparent material so the renderer stays in the blended pass
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("quake_shockwave"), geometry, [transparentMaterial])
		// Render both sides so the shell reads at every viewing angle
		this.drawState.cullFace = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		this.modelView.value = state.modelView
		// Seconds within the day, wrapped to keep float32 precision in the shader
		this.time.value = (state.realTime.julian % 1) * 86400
	}
}
